import { useCallback, useEffect, useState } from "react";

/**
 * Whether the navigation rail is expanded.
 *
 * Remembered per browser rather than per account: it is a preference about this
 * screen, not about the person. Someone on a wide desktop and a small laptop
 * wants different answers, and a server round-trip would make the rail flicker
 * to the wrong width on every load.
 */

const STORAGE_KEY = "astrum.rail.expanded";

/**
 * Read synchronously during the first render.
 *
 * Deliberately not an effect. Setting this after mount would render the
 * collapsed rail, then jump to expanded on the next frame — visible on every
 * single page load, which is worse than the cost of touching localStorage
 * during render.
 */
function readStored(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    // Private browsing and blocked storage both throw here. Collapsed is the
    // safe default: it is the layout the app was designed around.
    return false;
  }
}

/** Matches the `max-width: 900px` breakpoint where the rail gives way to tabs. */
const NARROW_PX = 900;

/*
 * Measured from the document element rather than matchMedia.
 *
 * Two reasons. A width of 0 — which happens while a pane is mid-navigation —
 * satisfies `max-width: 900px`, so matchMedia reports "narrow" for a viewport
 * that is nothing of the sort, and no `change` event follows when the real
 * width arrives, so the misreading sticks. And `clientWidth` is the width the
 * CSS breakpoint is actually resolving against, which `window.innerWidth` is
 * not always the same as inside an embedded panel.
 *
 * A zero width means "not measured yet", not "narrow".
 */
function isNarrow(): boolean {
  const width = document.documentElement.clientWidth || window.innerWidth;
  return width > 0 && width <= NARROW_PX;
}

export function useRail() {
  const [preference, setPreference] = useState(readStored);
  const [narrow, setNarrow] = useState(() => typeof window !== "undefined" && isNarrow());

  const toggle = useCallback(() => {
    setPreference((previous) => {
      const next = !previous;
      try {
        window.localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        // The rail still works for this session; it just will not be
        // remembered. Not worth surfacing.
      }
      return next;
    });
  }, []);

  /*
   * Observed on the document element rather than through matchMedia or a
   * `resize` listener.
   *
   * Both of those proved unreliable in an embedded/panel context: matchMedia
   * treats a transient zero width as "narrow" and then never corrects itself,
   * and `resize` does not always fire when the containing pane changes size
   * rather than the window. A ResizeObserver watches the element actually being
   * laid out, so it reports the width the CSS breakpoint is using.
   */
  useEffect(() => {
    const apply = () => setNarrow(isNarrow());
    apply();

    const observer = new ResizeObserver(apply);
    observer.observe(document.documentElement);
    window.addEventListener("resize", apply);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", apply);
    };
  }, []);

  /*
   * Below 900px the rail is replaced by a bottom tab bar, so an expanded rail
   * would only widen a grid column behind a hidden element.
   *
   * Suppressed here rather than by writing `false` to storage: narrowing the
   * window is not the person changing their mind, and clobbering the stored
   * value would mean a single visit on a small screen silently reset a
   * preference they set on a large one.
   */
  return { expanded: preference && !narrow, toggle };
}
